import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

import type {
  MessageDataType,
  MessageDataRoleType,
} from 'common/components/CopyMagic/CopyMagic.types';

import {
  DEV_RESULT_STUB_ACTIVE,
  DEV_RESULT_STUB,
  MESSAGE_DATA_ROLES,
} from 'common/components/CopyMagic/constants';
import { useMessagesContext } from 'common/components/CopyMagic/contexts/MessagesContext';
import { useUiResourceContext, useConfigContext } from 'common/components/CopyMagic/contexts';
import useGeneratorTrackingCallback from 'common/components/CopyMagic/hooks/useGeneratorTrackingCallback';
import useChatSessionId from 'common/components/CopyMagic/hooks/useChatSessionId';

import { createClaims, generateResult } from 'common/components/CopyMagic/api';
import copyMagicLog from 'common/components/CopyMagic/shared/copyMagicLog';
import getErrorMessageFor from 'common/components/CopyMagic/api/helpers/getErrorMessageFor';
import getErrorCode from 'common/components/CopyMagic/api/helpers/getErrorCode';
import piiSanitizer from 'common/components/CopyMagic/shared/piiSanitizer';

const isModerated = (error: unknown) => getErrorCode(error) === 'FLAGGED_CONTENT';

const MAX_MESSAGE_HISTORY = 3; // + the newly submitted message(s)

export type MessageRequestResultType =
  | (MessageDataType & { id: string; contentId?: string })
  | null;

type PrepareRequestDataInput = {
  sessionId: string;
};

type PrepareRequestDataOutput = {
  sessionId?: string;
  contentId?: string;
  requestSentAt?: string;
};

const prepareRequestData = ({ sessionId }: PrepareRequestDataInput): PrepareRequestDataOutput => ({
  sessionId,
  contentId: uuidv4(),
  requestSentAt: new Date().toISOString(),
});

const useMessageRequestBehavior = () => {
  const { loading, setLoading, cancelPendingRequest, setRequestSource, setError } =
    useUiResourceContext();
  const { generatorName, initialPrompt } = useConfigContext();
  const { getMessageDatas, setNewMessages } = useMessagesContext();
  const trackPromptResponse = useGeneratorTrackingCallback('useTrackPromptResponse');
  const { getChatSessionId } = useChatSessionId();

  const requestAndSetNewMessages = async (
    newMessageDatasUnsafe: MessageDataType[],
  ): Promise<MessageRequestResultType | null> => {
    if (loading) return null;
    const clientStartTime = performance.now();
    const chatSessionId = getChatSessionId();
    const requestData = prepareRequestData({ sessionId: chatSessionId });
    const { contentId } = requestData;
    let responseData = null;

    const historicalMessages = getMessageDatas(MAX_MESSAGE_HISTORY);
    const newMessageDatas: MessageDataType[] = newMessageDatasUnsafe.map((item) => ({
      ...item,
      content: piiSanitizer(item.content),
    }));
    const requestMessages: MessageDataType[] = [
      ...(initialPrompt ? [{ content: initialPrompt, role: MESSAGE_DATA_ROLES.SYSTEM }] : []),
      ...historicalMessages,
      ...newMessageDatas,
    ];

    cancelPendingRequest();
    const requestSource = axios.CancelToken.source();
    setRequestSource(requestSource);
    setLoading(true);
    setError(null);
    setNewMessages(newMessageDatas);

    try {
      if (DEV_RESULT_STUB_ACTIVE) {
        responseData = DEV_RESULT_STUB[generatorName] as MessageRequestResultType;
      } else {
        const { token, url } = await createClaims(generatorName, requestSource);
        responseData = await generateResult<MessageRequestResultType>({
          token,
          url,
          requestData: {
            ...requestData,
            messages: requestMessages,
          },
          requestSource,
        });
      }

      if (
        !(
          responseData?.content &&
          responseData?.role &&
          Object.values(MESSAGE_DATA_ROLES).includes(responseData.role as MessageDataRoleType)
        )
      ) {
        throw new Error(`Invalid response data: ${JSON.stringify(responseData)}`);
      }
      trackPromptResponse({
        isSuccess: true,
        isModerated: false,
        result: responseData,
        chatSessionId,
        clientDurationInSecs: (performance.now() - clientStartTime) / 1000,
      });

      setNewMessages([
        {
          role: responseData.role as MessageDataRoleType,
          content: responseData.content,
          contentId,
        },
      ]);
    } catch (error) {
      if (axios.isCancel(error)) {
        copyMagicLog('Request cancelled');
      } else {
        trackPromptResponse({
          isSuccess: false,
          isModerated: isModerated(error),
          chatSessionId,
          clientDurationInSecs: (performance.now() - clientStartTime) / 1000,
        });
        setError(getErrorMessageFor(error));
        copyMagicLog(error, { level: 'error' });
      }
    }

    setLoading(false);
    return responseData;
  };

  return {
    requestAndSetNewMessages,
  };
};

export default useMessageRequestBehavior;
